import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface PositionOption {
  id: number;
  position_code: string;
  position_title: string;
}

interface PerformanceReviewPolicy {
  id: number;
  position_id: number;
  position: PositionOption | null;
  approval_levels: number;
}

interface PolicyForm {
  position_id: string | number;
  approval_levels: string;
}

const emptyForm: PolicyForm = { position_id: '', approval_levels: '1' };

export function PerformanceReviewPolicies() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<PerformanceReviewPolicy[] | null>(null);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [editing, setEditing] = useState<'new' | PerformanceReviewPolicy | null>(null);
  const [form, setForm] = useState<PolicyForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<PerformanceReviewPolicy[]>('/performance-review-policies').then((res) => setItems(res.data));

  useEffect(() => {
    load();
    api.get<PositionOption[]>('/positions').then((res) => setPositions(res.data));
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: PerformanceReviewPolicy) => {
    setForm({ position_id: item.position_id, approval_levels: String(item.approval_levels) });
    setErrors({});
    setEditing(item);
  };

  const set =
    (key: keyof PolicyForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/performance-review-policies', form);
      else if (editing) await api.put(`/performance-review-policies/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: PerformanceReviewPolicy) => {
    if (!(await confirmDelete(`Delete the policy for "${item.position?.position_title}"?`))) return;
    try {
      await api.delete(`/performance-review-policies/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Performance Review Policies</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Position</th>
                <th>Approval Levels</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.position?.position_title ?? '—'}</td>
                  <td className="muted">{item.approval_levels === 0 ? 'None (auto-finalized)' : item.approval_levels}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted center">
                    No performance review policies configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal
          title={editing === 'new' ? 'Add performance review policy' : 'Edit performance review policy'}
          onClose={() => setEditing(null)}
        >
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Position *
              <select value={form.position_id} onChange={set('position_id')} required>
                <option value="">Select…</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.position_code} — {p.position_title}
                  </option>
                ))}
              </select>
              {errors.position_id && <small className="error">{errors.position_id[0]}</small>}
            </label>
            <label className="span-2">
              Workflow Approval Levels *
              <input type="number" step="1" min="0" max="10" value={form.approval_levels} onChange={set('approval_levels')} required />
              <small className="muted">
                How many managers must approve, in order — 1: manager, 2: manager's manager, etc. 0 means no approval is
                required; the review is finalized automatically.
              </small>
              {errors.approval_levels && <small className="error">{errors.approval_levels[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
